/** Shared presentational helpers — web mirror of `Sources/Views/UIHelpers.swift`
 * (StatusPill, EmptyState, relative-time formatting, task-status color/label). */

import type { CSSProperties, ReactNode } from 'react';
import { taskStatus, type TaskStatus } from '../models/factory';

/** A small colored capsule label. `color` is any CSS var/color string. */
export function StatusPill({
  text,
  color,
  icon,
}: {
  text: string;
  color: string;
  icon?: string;
}) {
  return (
    <span className="pill" style={{ background: color } as CSSProperties}>
      {icon ? <span aria-hidden>{icon}</span> : null}
      {text}
    </span>
  );
}

export function EmptyState({ icon, title, message }: { icon: string; title: string; message: string }) {
  return (
    <div className="empty">
      <div className="empty__icon" aria-hidden>
        {icon}
      </div>
      <div className="empty__title">{title}</div>
      <div className="caption" style={{ marginTop: 4 }}>
        {message}
      </div>
    </div>
  );
}

export function Section({
  header,
  headerIcon,
  warn,
  children,
}: {
  header?: string;
  headerIcon?: string;
  warn?: boolean;
  children: ReactNode;
}) {
  return (
    <section className="section">
      {header ? (
        <div className={`section__header${warn ? ' section__header--warn' : ''}`}>
          {headerIcon ? <span aria-hidden>{headerIcon}</span> : null}
          {header}
        </div>
      ) : null}
      <div className="card">{children}</div>
    </section>
  );
}

/** Compact relative time, e.g. "3m", "2h", "just now". */
export function relativeShort(iso: string | null | undefined): string {
  if (!iso) return '—';
  const then = Date.parse(iso);
  if (Number.isNaN(then)) return '—';
  const secs = Math.max(0, (Date.now() - then) / 1000);
  if (secs < 45) return 'just now';
  const mins = secs / 60;
  if (mins < 60) return `${Math.round(mins)}m ago`;
  const hours = mins / 60;
  if (hours < 24) return `${Math.round(hours)}h ago`;
  const days = hours / 24;
  return `${Math.round(days)}d ago`;
}

// Task status → color / icon / label (mirror of the Swift TaskStatus extension).

const STATUS_COLOR: Record<TaskStatus, string> = {
  queued: 'var(--text-secondary)',
  running: 'var(--blue)',
  blocked: 'var(--orange)',
  done: 'var(--green)',
  failed: 'var(--red)',
  cancelled: 'var(--text-tertiary)',
  unknown: 'var(--text-tertiary)',
};

const STATUS_ICON: Record<TaskStatus, string> = {
  queued: '○',
  running: '◐',
  blocked: '⏸',
  done: '✓',
  failed: '⚠',
  cancelled: '⊘',
  unknown: '•',
};

const STATUS_LABEL: Record<TaskStatus, string> = {
  queued: 'Queued',
  running: 'Running',
  blocked: 'Blocked',
  done: 'Done',
  failed: 'Failed',
  cancelled: 'Cancelled',
  unknown: 'Unknown',
};

export function statusColor(raw: string | null | undefined): string {
  return STATUS_COLOR[taskStatus(raw)];
}
export function statusIcon(raw: string | null | undefined): string {
  return STATUS_ICON[taskStatus(raw)];
}
export function statusLabel(raw: string | null | undefined): string {
  return STATUS_LABEL[taskStatus(raw)];
}
